import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    this.animateAddToCart();

    if (!event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} shipDate - The ship date display element.
 * @property {HTMLElement | undefined} saleNote - The sale note display element.
 * @property {HTMLTextAreaElement | undefined} saleNoteProperty - The sale note textarea for cart properties.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantHasBeenSelected = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Set initial state: button shows "Choose Variant", ship date and sale note are hidden
    this.#setInitialState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Sets the initial state on page load before any variant is selected
   */
  #setInitialState() {
    // Check if a variant is already selected (from URL parameter or default)
    const variantPicker = this.closest('.shopify-section, dialog, product-card')?.querySelector('variant-picker');
    const selectedOptions = variantPicker?.querySelectorAll('select option[selected], fieldset input:checked');
    const selectedCount = selectedOptions?.length || 0;

    // If all options are selected (complete variant selection), don't override the initial state
    if (this.#totalOptions(variantPicker) > 0 && selectedCount >= this.#totalOptions(variantPicker)) {
      this.#variantHasBeenSelected = true;
      return;
    }

    // For single-variant products (no variant picker), treat as if variant is selected
    if (!variantPicker) {
      this.#variantHasBeenSelected = true;
      return;
    }

    const { addToCartButtonContainer, shipDate, saleNote } = this.refs;
    const addToCartButton = addToCartButtonContainer?.refs?.addToCartButton;

    if (!addToCartButton) return;

    // Set button text to "Choose Variant" and disable it
    const textElement = addToCartButton.querySelector('.add-to-cart-text__content');
    if (textElement) {
      textElement.textContent = 'Choose Variant';
    }
    addToCartButton.disabled = true;

    // Hide ship date and sale note initially
    if (shipDate) {
      shipDate.classList.add('pdp-hidden');
    }
    if (saleNote) {
      saleNote.classList.add('pdp-hidden');
    }
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          window.dispatchEvent(new CartErrorEvent(this.id, response.message));

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs?.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs?.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    const variantPicker = this.closest('.shopify-section, dialog, product-card')?.querySelector('variant-picker');
    const selectedOption = variantPicker?.querySelector('select option[selected], fieldset input:checked');

    const primaryLocation = selectedOption?.dataset.primaryLocation;
    const secondaryLocation = selectedOption?.dataset.secondaryLocation;
    const inventoryQuantity = parseInt(selectedOption?.dataset.inventoryQuantity || '0', 10);

    let shouldDisable = false;
    if (primaryLocation === 'false' && secondaryLocation === 'false') {
      shouldDisable = true;
    } else if (!primaryLocation && !secondaryLocation && inventoryQuantity <= 0) {
      shouldDisable = true;
    } else if (event.detail.resource == null || event.detail.resource.available == false) {
      shouldDisable = true;
    }

    if (shouldDisable) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    this.#updateButtonStateFromVariant(event);

    variantId.value = event.detail.resource?.id ?? '';

    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = (event) => {
    this.#variantHasBeenSelected = true;
    this.refs.addToCartButtonContainer?.disable();
    this.#optimisticUpdate();
  };

  /**
   * Performs an optimistic update of button state using data attributes from selected options.
   * This eliminates the delay waiting for the network response.
   */
  #optimisticUpdate() {
    const variantPicker = this.closest('.shopify-section, dialog, product-card')?.querySelector('variant-picker');
    if (!variantPicker) return;

    const selectedOptionsCount = variantPicker.querySelectorAll('input:checked, select option[selected]').length;
    const hasCompleteSelection = this.#totalOptions(variantPicker) > 0 && selectedOptionsCount >= this.#totalOptions(variantPicker);

    const { variantId, addToCartButtonContainer, shipDate, saleNote: saleNoteElement, saleNoteProperty, klaviyoButtonContainer, addToCartWrapper } = this.refs;
    const addToCartButton = addToCartButtonContainer?.refs?.addToCartButton;
    if (!addToCartButton) return;

    const form = this.querySelector('form');
    if (!form) return;

    this.#clearShipDateMessage(form, shipDate);

    if (!hasCompleteSelection) {
      const textElement = addToCartButton.querySelector('.add-to-cart-text__content');
      if (textElement) {
        textElement.textContent = 'Choose Variant';
      }
      addToCartButton.disabled = true;
      if (variantId) variantId.value = '';

      if (shipDate) shipDate.classList.add('pdp-hidden');
      if (saleNoteElement) saleNoteElement.classList.add('pdp-hidden');
      if (saleNoteProperty) {
        saleNoteProperty.value = '';
        saleNoteProperty.removeAttribute('name');
      }
      if (klaviyoButtonContainer) klaviyoButtonContainer.classList.add('hidden');
      if (addToCartWrapper) addToCartWrapper.classList.remove('hidden');
      return;
    }

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked, select option[selected]'));
    const optionWithData = selectedOptions.reverse().find(opt =>
      opt instanceof HTMLElement &&
      opt.dataset.variantId &&
      (opt.dataset.primaryLocation !== undefined || opt.dataset.secondaryLocation !== undefined)
    );

    if (!optionWithData || !(optionWithData instanceof HTMLElement)) return;

    if (variantId && optionWithData.dataset.variantId) {
      variantId.value = optionWithData.dataset.variantId;
    }

    const primaryLocation = optionWithData.dataset.primaryLocation;
    const secondaryLocation = optionWithData.dataset.secondaryLocation;
    const availableToShipDate = optionWithData.dataset.availableToShipDate;
    const saleNote = optionWithData.dataset.saleNote;
    const inventoryQuantity = parseInt(optionWithData.dataset.inventoryQuantity || '0', 10);
    const isAvailable = optionWithData.dataset.optionAvailable === 'true';

    let buttonText = 'Add to Cart';
    let shouldDisable = false;
    let isPreOrder = false;
    let isSoldOut = false;

    if (primaryLocation === 'false' && secondaryLocation === 'false') {
      isSoldOut = true;
      buttonText = 'Sold Out';
      shouldDisable = true;
    } else if (!primaryLocation && !secondaryLocation && inventoryQuantity <= 0) {
      isSoldOut = true;
      buttonText = 'Sold Out';
      shouldDisable = true;
    } else if (primaryLocation === 'false' && secondaryLocation === 'true') {
      isPreOrder = true;
      buttonText = 'Pre-order';
    }

    const quantitySelector = form.querySelector('quantity-selector-component');

    if (klaviyoButtonContainer && addToCartWrapper) {
      if (isSoldOut) {
        klaviyoButtonContainer.classList.remove('hidden');
        addToCartWrapper.classList.add('hidden');
        if (quantitySelector) quantitySelector.classList.add('hidden');
      } else {
        klaviyoButtonContainer.classList.add('hidden');
        addToCartWrapper.classList.remove('hidden');
        if (quantitySelector) quantitySelector.classList.remove('hidden');
      }
    }

    const textElement = addToCartButton.querySelector('.add-to-cart-text__content');
    if (textElement) {
      textElement.textContent = buttonText;
    }

    addToCartButton.disabled = shouldDisable;

    if (isSoldOut) {
      if (shipDate) shipDate.classList.add('pdp-hidden');
      if (saleNoteElement) saleNoteElement.classList.add('pdp-hidden');
      if (saleNoteProperty) {
        saleNoteProperty.value = '';
        saleNoteProperty.removeAttribute('name');
      }
      return;
    }

    if (isPreOrder && availableToShipDate) {
      this.#setShipDateMessage(form, shipDate, availableToShipDate);
      if (shipDate) shipDate.classList.remove('pdp-hidden');
    } else if (isAvailable && inventoryQuantity > 0 && inventoryQuantity < 3) {
      this.#setLowInventoryWarning(shipDate, inventoryQuantity);
      if (shipDate) shipDate.classList.remove('pdp-hidden');
    } else {
      if (shipDate) shipDate.classList.add('pdp-hidden');
    }

    this.#updateSaleNote(saleNote, isPreOrder, saleNoteElement, saleNoteProperty);
  }

  /**
   * Updates button state based on variant metafields using Clear-Then-Set pattern
   * @param {VariantUpdateEvent} event
   */
  #updateButtonStateFromVariant(event) {
    const variantPicker = this.closest('.shopify-section, dialog, product-card')?.querySelector('variant-picker');
    if (!variantPicker) return;

    // Count how many options are currently selected
    const selectedOptionsCount = variantPicker.querySelectorAll('input:checked, select option[selected]').length;

    // Check if we have a complete variant selection
    const hasCompleteSelection = this.#totalOptions(variantPicker) > 0 && selectedOptionsCount >= this.#totalOptions(variantPicker);

    // Get variant data - only valid when all options are selected
    const variantId = event.detail.resource?.id;
    let selectedOption;

    if (variantId && hasCompleteSelection) {
      // For multi-option products, find the checked/selected option that matches this variant ID
      // This ensures we get the data from the correct option element
      const checkedWithVariantId = variantPicker.querySelector(`input:checked[data-variant-id="${variantId}"], select option[selected][data-variant-id="${variantId}"]`);

      if (checkedWithVariantId) {
        selectedOption = checkedWithVariantId;
      } else {
        // Fallback: find any option with this variant ID (might not be checked if DOM update timing issue)
        selectedOption = variantPicker.querySelector(`[data-variant-id="${variantId}"]`);
      }
    }

    // If no selected option found yet, try to find the most recently selected option with variant data
    if (!selectedOption && hasCompleteSelection) {
      const allSelectedOptions = Array.from(variantPicker.querySelectorAll('select option[selected], fieldset input:checked'));
      // Find the last selected option that has complete variant data (variant-id and location data)
      selectedOption = allSelectedOptions.reverse().find(opt =>
        opt instanceof HTMLElement &&
        opt.dataset.variantId &&
        (opt.dataset.primaryLocation !== undefined || opt.dataset.secondaryLocation !== undefined)
      );
    }

    // Final fallback: use first checked option (for partial selections)
    if (!selectedOption) {
      selectedOption = variantPicker.querySelector('select option[selected], fieldset input:checked');
    }

    if (!selectedOption || !(selectedOption instanceof HTMLElement)) return;

    // Only read variant-specific data if we have a complete selection
    const primaryLocation = hasCompleteSelection ? selectedOption.dataset.primaryLocation : undefined;
    const secondaryLocation = hasCompleteSelection ? selectedOption.dataset.secondaryLocation : undefined;
    const availableToShipDate = hasCompleteSelection ? selectedOption.dataset.availableToShipDate : undefined;
    const saleNote = hasCompleteSelection ? selectedOption.dataset.saleNote : undefined;
    const inventoryQuantity = hasCompleteSelection ? parseInt(selectedOption.dataset.inventoryQuantity || '0', 10) : 0;
    const isAvailable = hasCompleteSelection ? selectedOption.dataset.optionAvailable === 'true' : false;

    const { addToCartButtonContainer, shipDate, saleNote: saleNoteElement, saleNoteProperty, klaviyoButtonContainer, addToCartWrapper } = this.refs;
    const addToCartButton = addToCartButtonContainer?.refs?.addToCartButton;
    if (!addToCartButton) return;

    const form = this.querySelector('form');
    if (!form) return;

    // Always clear ship date first (Clear-Then-Set pattern)
    this.#clearShipDateMessage(form, shipDate);

    // If not all options are selected (multi-option product), show "Choose Variant" and return
    if (!hasCompleteSelection) {
      const textElement = addToCartButton.querySelector('.add-to-cart-text__content');
      if (textElement) {
        textElement.textContent = 'Choose Variant';
      }
      addToCartButton.disabled = true;

      // Hide ship date and sale note
      if (shipDate) {
        shipDate.classList.add('pdp-hidden');
      }
      if (saleNoteElement) {
        saleNoteElement.classList.add('pdp-hidden');
      }
      if (saleNoteProperty) {
        saleNoteProperty.value = '';
        saleNoteProperty.removeAttribute('name');
      }

      // Hide Klaviyo button and show Add to Cart wrapper
      if (klaviyoButtonContainer) {
        klaviyoButtonContainer.classList.add('hidden');
      }
      if (addToCartWrapper) {
        addToCartWrapper.classList.remove('hidden');
      }

      // Keep quantity selector visible for partial selections
      const quantitySelector = form.querySelector('quantity-selector-component');
      if (quantitySelector) {
        quantitySelector.classList.remove('hidden');
      }

      return;
    }

    let buttonText = 'Add to Cart';
    let shouldDisable = false;
    let isPreOrder = false;
    let isSoldOut = false;

    // Determine button state based on inventory and location
    if (primaryLocation === 'false' && secondaryLocation === 'false') {
      isSoldOut = true;
      buttonText = 'Sold Out';
      shouldDisable = true;
    } else if (!primaryLocation && !secondaryLocation && inventoryQuantity <= 0) {
      isSoldOut = true;
      buttonText = 'Sold Out';
      shouldDisable = true;
    } else if (primaryLocation === 'false' && secondaryLocation === 'true') {
      isPreOrder = true;
      buttonText = 'Pre-order';
    }

    // Show/hide Klaviyo button and Add to Cart button based on sold out state
    const quantitySelector = form.querySelector('quantity-selector-component');

    if (klaviyoButtonContainer && addToCartWrapper) {
      if (isSoldOut) {
        klaviyoButtonContainer.classList.remove('hidden');
        addToCartWrapper.classList.add('hidden');
        if (quantitySelector) {
          quantitySelector.classList.add('hidden');
        }
      } else {
        klaviyoButtonContainer.classList.add('hidden');
        addToCartWrapper.classList.remove('hidden');
        if (quantitySelector) {
          quantitySelector.classList.remove('hidden');
        }
      }
    }

    // Update button text
    const textElement = addToCartButton.querySelector('.add-to-cart-text__content');
    if (textElement) {
      textElement.textContent = buttonText;
    }

    // Enable or disable button based on availability
    if (shouldDisable) {
      addToCartButton.disabled = true;
    } else {
      addToCartButton.disabled = false;
    }

    // Hide ship date and sale note when product is sold out
    if (isSoldOut) {
      if (shipDate) {
        shipDate.classList.add('pdp-hidden');
      }
      if (saleNoteElement) {
        saleNoteElement.classList.add('pdp-hidden');
      }
      if (saleNoteProperty) {
        saleNoteProperty.value = '';
        saleNoteProperty.removeAttribute('name');
      }
      return;
    }

    // Show/hide ship date based on conditions (only if not sold out)
    if (isPreOrder && availableToShipDate) {
      // Show ship date for pre-order variants
      this.#setShipDateMessage(form, shipDate, availableToShipDate);
      if (shipDate) {
        shipDate.classList.remove('pdp-hidden');
      }
    } else if (isAvailable && inventoryQuantity > 0 && inventoryQuantity < 3) {
      // Show low inventory warning
      this.#setLowInventoryWarning(shipDate, inventoryQuantity);
      if (shipDate) {
        shipDate.classList.remove('pdp-hidden');
      }
    } else {
      // Hide ship date if not pre-order or no ship date
      if (shipDate) {
        shipDate.classList.add('pdp-hidden');
      }
    }

    // Update sale note with conditional visibility (only if not sold out)
    this.#updateSaleNote(saleNote, isPreOrder, saleNoteElement, saleNoteProperty);
  }

  /**
   * Updates sale note display and cart property
   * @param {string | undefined} saleNote
   * @param {boolean} isPreOrder
   * @param {HTMLElement | undefined} saleNoteElement
   * @param {HTMLTextAreaElement | undefined} saleNoteProperty
   */
  #updateSaleNote(saleNote, isPreOrder, saleNoteElement, saleNoteProperty) {
    // Clear and hide if no sale note or empty string
    if (!saleNote || saleNote.trim() === '') {
      if (saleNoteElement) {
        saleNoteElement.textContent = '';
        saleNoteElement.classList.add('pdp-hidden');
      }
      if (saleNoteProperty) {
        saleNoteProperty.value = '';
        saleNoteProperty.removeAttribute('name');
      }
      return;
    }

    // Check if sale note contains any variation of "pre-order" (case insensitive)
    const lowerCaseSaleNote = saleNote.toLowerCase();
    const containsPreOrder = lowerCaseSaleNote.includes('preorder') ||
                             lowerCaseSaleNote.includes('pre-order') ||
                             lowerCaseSaleNote.includes('pre order');

    // Clear and hide if sale note mentions pre-order but variant is actually available (not pre-order)
    if (containsPreOrder && !isPreOrder) {
      if (saleNoteElement) {
        saleNoteElement.textContent = '';
        saleNoteElement.classList.add('pdp-hidden');
      }
      if (saleNoteProperty) {
        saleNoteProperty.value = '';
        saleNoteProperty.removeAttribute('name');
      }
      return;
    }

    // Show sale note with content
    if (saleNoteElement) {
      const textElement = saleNoteElement.querySelector('.sale-note-text');
      if (textElement) {
        textElement.textContent = saleNote;
      } else {
        saleNoteElement.textContent = saleNote;
      }
      saleNoteElement.classList.remove('pdp-hidden');
    }

    if (saleNoteProperty) {
      saleNoteProperty.value = saleNote;
      saleNoteProperty.setAttribute('name', 'properties[Order Note]');
    }
  }

  /**
   * Clears ship date message and removes hidden input (Step 1: Clear)
   * @param {HTMLFormElement} form
   * @param {HTMLElement | undefined} shipDateElement
   */
  #clearShipDateMessage(form, shipDateElement) {
    const existingInput = form.querySelector('.ship_date_input');
    if (existingInput) {
      existingInput.remove();
    }

    if (shipDateElement) {
      shipDateElement.textContent = '';
    }
  }

  /**
   * Sets ship date message and creates hidden input (Step 2: Set)
   * @param {HTMLFormElement} form
   * @param {HTMLElement | undefined} shipDateElement
   * @param {string} shipDate
   */
  #setShipDateMessage(form, shipDateElement, shipDate) {
    const message = `Expected To Ship: ${shipDate}`;

    if (shipDateElement) {
      shipDateElement.innerHTML = `<strong>Expected To Ship:</strong> ${shipDate}`;
    }

    const hiddenInput = document.createElement('input');
    hiddenInput.type = 'hidden';
    hiddenInput.name = 'properties[Shipping Note]';
    hiddenInput.className = 'ship_date_input';
    hiddenInput.value = message;
    form.appendChild(hiddenInput);
  }

  /**
   * Sets low inventory warning message
   * @param {HTMLElement | undefined} shipDateElement
   * @param {number} inventoryQuantity
   */
  #setLowInventoryWarning(shipDateElement, inventoryQuantity) {
    if (shipDateElement) {
      shipDateElement.innerHTML = `Only ${inventoryQuantity} Left!`;
    }
  }

  #totalOptions(variantPicker) {
    // Count total options vs selected options to determine if we have a complete selection
    let totalOptions = variantPicker?.querySelectorAll('.variant-option').length || 0;
    // Check if limited and core colors are present. If yes reduce total options quantity, because it's the same option
    if(variantPicker?.querySelector('[data-limited-colors]') && variantPicker.querySelector('[data-core-colors]')) {
      totalOptions = totalOptions - 1;
    };

    return totalOptions;
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
